import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import useSWR from "swr";
import "./index.scss";
import ArrowDownIcon from "../../svgs/arrow-down.svg";
import DHList from "./components/DHList";
import DHDetails from "./components/DHDetails";
import { inventoryFetcher } from "../../network/InventoryService";

const DistributionManagement = () => {
  const location = useLocation();
  const history = useHistory();

  const [selectedDH, setSelectedDH] = useState(null);
  const [showDHDetails, setShowDHDetails] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("view") === "details";
  });
  const [circleFilter, setCircleFilter] = useState("");
  const [territoryFilter, setTerritoryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const localUserType = JSON.parse(localStorage.getItem("currUserType"));
  const canFilter = !["territory_manager", "agency_user"].includes(
    localUserType
  );
  const clustersApiUrl = `${process.env.REACT_APP_API_URL}/api/organizations/api/v1/clusters?is_dropdown=true`;

  const { data: clustersResponse, error: clustersError } = useSWR(
    clustersApiUrl,
    inventoryFetcher,
    {
      fallbackData: [],
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );
  const clusters = clustersResponse?.data || [];
  const clustersLoading = !clustersResponse && !clustersError;

  const territoriesApiUrl = useMemo(() => {
    if (!circleFilter) return null;

    const params = new URLSearchParams({
      cluster: circleFilter,
      is_dropdown: true,
    });

    return `${
      process.env.REACT_APP_API_URL
    }/api/organizations/api/v1/territories?${params.toString()}`;
  }, [circleFilter]);

  const { data: territoriesResponse, error: territoriesError } = useSWR(
    territoriesApiUrl,
    inventoryFetcher,
    {
      fallbackData: [],
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const territories = territoriesResponse?.data || [];
  const territoriesLoading = !territoriesResponse && !territoriesError;

  const circleOptions = [
    {
      value: "",
      label: clustersLoading
        ? "Loading circles..."
        : clustersError
          ? "Error loading circles"
          : "Circle Filter",
    },
    ...clusters.map((cluster) => ({
      value: cluster.id.toString(),
      label: cluster.name,
    })),
  ];

  const territoryOptions = [
    {
      value: "",
      label: !circleFilter
        ? "Select circle first"
        : territoriesLoading
          ? "Loading territories..."
          : territoriesError
            ? "Error loading territories"
            : "Territory Filter",
    },
    ...territories.map((territory) => ({
      value: territory.id.toString(),
      label: territory.name,
    })),
  ];

  const handleCircleChange = (e) => {
    const newCircleValue = e.target.value;
    setCircleFilter(newCircleValue);
    setTerritoryFilter("");
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentView = params.get("view");
    if (!currentView && !showDHDetails) {
      params.set("view", "list");
      history.replace(`${location.pathname}?${params.toString()}`);
    }
  }, [history, showDHDetails, location.search, location.pathname]);

  const handleDHView = (warehouse) => {
    setSelectedDH(warehouse);
    setShowDHDetails(true);
    const params = new URLSearchParams(location.search);
    params.set("view", "details");
    params.set("dhId", warehouse.id);
    history.push(`${location.pathname}?${params.toString()}`);
  };

  const handleBackToList = () => {
    setShowDHDetails(false);
    setSelectedDH(null);
    const params = new URLSearchParams(location.search);
    params.set("view", "list");
    params.delete("dhId");
    params.delete("dhDetailTab");
    history.push(`${location.pathname}?${params.toString()}`);
  };

  if (showDHDetails && selectedDH) {
    return (
      <DHDetails distributionHouse={selectedDH} onBack={handleBackToList} />
    );
  }

  return (
    <div className="content-section">
      <div className="content-header">
        <div></div>
        {canFilter && (
          <>
            <div className="filters-section">
              <div className="select-wrapper">
                <select
                  value={circleFilter}
                  onChange={handleCircleChange}
                  className="select-field"
                  disabled={clustersLoading || clustersError}
                >
                  {circleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <img
                  src={ArrowDownIcon}
                  alt="Select Icon"
                  className="select-icon"
                />
              </div>
              <div className="select-wrapper">
                <select
                  value={territoryFilter}
                  onChange={(e) => setTerritoryFilter(e.target.value)}
                  className="select-field"
                  disabled={
                    !circleFilter || territoriesLoading || territoriesError
                  }
                >
                  {territoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <img
                  src={ArrowDownIcon}
                  alt="Select Icon"
                  className="select-icon"
                />
              </div>
            </div>
          </>
        )}
      </div>
      <div className="content-body">
        <DHList
          onDHView={handleDHView}
          searchTerm={""}
          circleFilter={circleFilter}
          territoryFilter={territoryFilter}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default DistributionManagement;
